import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Pagination from '../../component/Admin/Pagination';
import { FaPlusCircle, FaTrashAlt, FaSearch, FaMapMarkerAlt, FaCrosshairs } from 'react-icons/fa';
import Loading from '../../component/Loading';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import { adminTable } from '../../component/Admin/adminTable';
import LokasiMapPicker from '../../component/Admin/LokasiMapPicker';

const Lokasi = ({ data }) => {
    const header = { withCredentials: true };
    const [listLokasi, setListLokasi] = useState([]);
    const [meta, setMeta] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Form coordinate state (controlled so map & inputs stay in sync)
    const [formLat, setFormLat] = useState('');
    const [formLng, setFormLng] = useState('');
    const [formRadius, setFormRadius] = useState(100);

    useEffect(() => {
        getListLokasi();
        // eslint-disable-next-line
    }, [page, limit]);

    const getListLokasi = () => {
        axios.get(`api/lokasi?page=${page}&limit=${limit}`, header)
            .then((response) => {
                setMeta(response.data.meta);
                setListLokasi(response.data.items);
            })
            .catch((error) => console.error('Error fetching lokasi:', error));
    };

    const filtered = useMemo(() => {
        if (!searchTerm) return listLokasi;
        const q = searchTerm.toLowerCase();
        return listLokasi.filter((item) =>
            item.nama?.toLowerCase().includes(q) ||
            item.jaringan?.toLowerCase().includes(q)
        );
    }, [listLokasi, searchTerm]);

    const openAdd = () => {
        setModalData(null);
        setFormLat('');
        setFormLng('');
        setFormRadius(100);
        setModalIsOpen(true);
    };

    const openEdit = (item) => {
        setModalData(item);
        setFormLat(item.latitude || '');
        setFormLng(item.longitude || '');
        setFormRadius(item.radius || 100);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalData(null);
    };

    const handleMapChange = useCallback(({ latitude, longitude }) => {
        setFormLat(latitude);
        setFormLng(longitude);
    }, []);

    const handleUseMyLocation = () => {
        if (!navigator.geolocation) {
            Swal.fire({ icon: 'warning', text: 'Browser tidak mendukung geolokasi' });
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setFormLat(pos.coords.latitude.toFixed(7));
                setFormLng(pos.coords.longitude.toFixed(7));
            },
            () => Swal.fire({ icon: 'warning', text: 'Gagal mendapatkan lokasi. Pastikan izin lokasi aktif.' }),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleDelete = async (id, event) => {
        event.stopPropagation();
        const result = await Swal.fire({
            title: 'Hapus lokasi?',
            text: 'Lokasi yang dihapus tidak dapat dikembalikan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonText: 'Batal',
            confirmButtonText: 'Ya, Hapus',
        });
        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            await axios.delete(`api/lokasi/${id}`, header);
            await Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Lokasi dihapus', timer: 1500 });
            getListLokasi();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Gagal', text: error.response?.data?.message || 'Gagal menghapus' });
        } finally {
            setLoading(false);
        }
    };

    const handleMigrate = async () => {
        const result = await Swal.fire({
            title: 'Impor dari Devisi?',
            text: 'Membuat lokasi dari data devisi dan men-set lokasi user sesuai devisi lama. Aman dijalankan ulang.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ya, Impor',
            cancelButtonText: 'Batal',
        });
        if (!result.isConfirmed) return;

        setLoading(true);
        try {
            const res = await axios.post('api/lokasi/migrate-from-devisi', {}, header);
            await Swal.fire({
                icon: 'success',
                title: 'Migrasi selesai',
                text: `Lokasi baru: ${res.data.createdLokasi}, assign user: ${res.data.assignedUserLokasi}`,
            });
            getListLokasi();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error.response?.data?.message || 'Gagal migrasi',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formLat || !formLng) {
            Swal.fire({ icon: 'warning', text: 'Klik peta atau gunakan lokasi saya untuk menentukan titik koordinat.' });
            return;
        }
        setLoading(true);
        const formData = new FormData(event.target);
        const payload = Object.fromEntries(formData.entries());
        payload.latitude = formLat;
        payload.longitude = formLng;
        payload.radius = Number(formRadius);
        payload.active = payload.active === 'true' || payload.active === 'on';

        try {
            if (modalData?.id_lokasi) {
                await axios.patch(`api/lokasi/${modalData.id_lokasi}`, payload, header);
            } else {
                await axios.post('api/lokasi', payload, header);
            }
            await Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Lokasi disimpan', timer: 1500 });
            getListLokasi();
            closeModal();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error.response?.data?.message || 'Gagal menyimpan lokasi',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading && <Loading />}
            <div className={adminTable.pageHeader}>
                <div>
                    <h1 className={adminTable.pageTitle}>
                        <FaMapMarkerAlt className="text-[#1B3C33]" /> Manajemen Lokasi
                    </h1>
                    <p className={adminTable.pageSubtitle}>Lokasi absensi bisa dipakai banyak user</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleMigrate}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-[#1B3C33] text-[#1B3C33] rounded-lg hover:bg-gray-50"
                    >
                        Impor dari Devisi
                    </button>
                    <button type="button" onClick={openAdd} className={adminTable.btnPrimary}>
                        <FaPlusCircle /> Tambah Lokasi
                    </button>
                </div>
            </div>

            <div className={adminTable.searchWrap}>
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Cari lokasi..."
                    className={adminTable.searchInput}
                />
            </div>

            <div>
                <div className={adminTable.list}>
                    {filtered.length > 0 ? (
                        filtered.map((item) => (
                            <div
                                key={item.id_lokasi}
                                className={adminTable.listItemClickable}
                                onClick={() => openEdit(item)}
                            >
                                <div className={adminTable.listTop}>
                                    <div className="min-w-0 flex-1">
                                        <div className={adminTable.listTitle}>{item.nama}</div>
                                        <div className={adminTable.listSub}>{item.radius} m · {item.active ? 'Aktif' : 'Nonaktif'}{item.jaringan ? ` · ${item.jaringan}` : ''}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className={`${adminTable.actionBtn} text-red-500 hover:bg-red-50`}
                                        onClick={(e) => handleDelete(item.id_lokasi, e)}
                                    >
                                        <FaTrashAlt />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={adminTable.listEmpty}>Belum ada data lokasi</div>
                    )}
                </div>

                <div className={`${adminTable.card} ${adminTable.desktopOnly}`}>
                    <div className={adminTable.scroll}>
                        <table className={adminTable.table}>
                            <thead className={adminTable.thead}>
                                <tr>
                                    <th className={adminTable.th}>Nama</th>
                                    <th className={adminTable.th}>Koordinat</th>
                                    <th className={adminTable.th}>Radius</th>
                                    <th className={adminTable.th}>Jaringan</th>
                                    <th className={adminTable.th}>Status</th>
                                    <th className={adminTable.thCenter}>Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((item) => (
                                    <tr
                                        key={item.id_lokasi}
                                        className={adminTable.trClickable}
                                        onClick={() => openEdit(item)}
                                    >
                                        <td className={`${adminTable.td} font-medium`}>{item.nama}</td>
                                        <td className={adminTable.tdMuted}>
                                            {item.latitude}, {item.longitude}
                                        </td>
                                        <td className={adminTable.td}>{item.radius} m</td>
                                        <td className={adminTable.td}>{item.jaringan || '-'}</td>
                                        <td className={adminTable.td}>
                                            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${item.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {item.active ? 'Aktif' : 'Nonaktif'}
                                            </span>
                                        </td>
                                        <td className={adminTable.tdCenter}>
                                            <button
                                                type="button"
                                                className={`${adminTable.actionBtn} text-red-500 hover:bg-red-50`}
                                                onClick={(e) => handleDelete(item.id_lokasi, e)}
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className={adminTable.empty}>
                                            Belum ada data lokasi
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="mt-3">
                    <Pagination
                        currentPage={page}
                        totalPages={meta.totalPages}
                        limitPage={(e) => setLimit(Number(e.target.value))}
                        selectPage={setPage}
                        nextPage={() => page < meta.totalPages && setPage(page + 1)}
                        prevPage={() => page > 1 && setPage(page - 1)}
                    />
                </div>
            </div>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                ariaHideApp={false}
                className="fixed inset-0 flex items-center justify-center p-4 z-50"
                overlayClassName="fixed inset-0 bg-black/50 z-40"
            >
                <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                    <h2 className="text-lg font-semibold mb-4">
                        {modalData ? 'Edit Lokasi' : 'Tambah Lokasi'}
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div>
                            <label className="text-xs font-semibold text-gray-600 uppercase">Nama Lokasi</label>
                            <input name="nama" required defaultValue={modalData?.nama || ''} className="w-full p-3 mt-1 bg-gray-100 rounded" />
                        </div>

                        {/* Map picker */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase">Pilih Titik di Peta</label>
                                <button
                                    type="button"
                                    onClick={handleUseMyLocation}
                                    className="inline-flex items-center gap-1 text-xs text-[#1B3C33] hover:underline"
                                >
                                    <FaCrosshairs /> Lokasi saya
                                </button>
                            </div>
                            <LokasiMapPicker
                                latitude={formLat}
                                longitude={formLng}
                                radius={formRadius}
                                onChange={handleMapChange}
                            />
                            <p className="text-xs text-gray-500 mt-1">Klik peta untuk menentukan titik koordinat. Lingkaran menunjukkan radius.</p>
                        </div>

                        {/* Coordinate display (read-only but visible) */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">Latitude</label>
                                <input
                                    value={formLat}
                                    onChange={(e) => setFormLat(e.target.value)}
                                    placeholder="Klik peta"
                                    className="w-full p-3 mt-1 bg-gray-100 rounded text-base"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">Longitude</label>
                                <input
                                    value={formLng}
                                    onChange={(e) => setFormLng(e.target.value)}
                                    placeholder="Klik peta"
                                    className="w-full p-3 mt-1 bg-gray-100 rounded text-base"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-gray-600 uppercase">Radius (meter)</label>
                            <input
                                name="radius"
                                type="number"
                                min="1"
                                required
                                value={formRadius}
                                onChange={(e) => setFormRadius(Number(e.target.value))}
                                className="w-full p-3 mt-1 bg-gray-100 rounded"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-600 uppercase">Jaringan / IP (opsional)</label>
                            <input name="jaringan" placeholder="192.168.1.0" defaultValue={modalData?.jaringan || ''} className="w-full p-3 mt-1 bg-gray-100 rounded" />
                            <p className="text-xs text-gray-500 mt-1">Kosongkan jika tidak perlu cek WiFi/LAN</p>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-600 uppercase">Status</label>
                            <select name="active" defaultValue={modalData?.active === false ? 'false' : 'true'} className="w-full p-3 mt-1 bg-gray-100 rounded">
                                <option value="true">Aktif</option>
                                <option value="false">Nonaktif</option>
                            </select>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button type="submit" className="flex-1 py-3 bg-[#1B3C33] text-white rounded-lg uppercase text-sm font-medium">Simpan</button>
                            <button type="button" onClick={closeModal} className="flex-1 py-3 bg-gray-500 text-white rounded-lg uppercase text-sm font-medium">Batal</button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
};

export default Lokasi;
